import type { IUser } from '../models/User';

/**
 * Scope-to-role mapping used when parsing Auth0 scopes into application roles.
 * Each entry maps a scope substring (present in the OAuth access token) to
 * the internal role name used throughout the application.
 *
 * Order matters: scopes are checked in definition order, and every matching
 * scope produces a role entry.  A user who has both `admin:propipe` and
 * `admin:company` will receive `['superadmin', 'admin']`.
 */
export const SCOPE_ROLE_MAP: ReadonlyArray<{ scope: string; role: string }> = [
  { scope: 'admin:propipe', role: 'superadmin' },
  { scope: 'admin:company', role: 'admin' },
  { scope: 'user:company', role: 'viewer' },
  { scope: 'admin:metrics', role: 'metrics' },
];

/**
 * Parse a space-delimited OAuth scope string and return all matching
 * application roles.  Returns an empty array when no recognised scopes
 * are present.
 */
export function parseRolesFromScopes(scopeString: string): string[] {
  if (!scopeString) return [];
  const roles: string[] = [];
  for (const entry of SCOPE_ROLE_MAP) {
    if (scopeString.indexOf(entry.scope) !== -1) {
      roles.push(entry.role);
    }
  }
  return roles;
}

/**
 * Check whether a user holds **at least one** of the required roles.
 *
 * @param user      The user object (must expose a `roles` array).
 *                  Passing `null`/`undefined` returns `false`.
 * @param required  One or more role strings the caller will accept.
 *
 * @returns `true` when the user has at least one of the listed roles.
 *
 * @example
 * ```ts
 * // single role
 * hasAnyRole(user, 'superadmin');
 *
 * // multiple acceptable roles
 * hasAnyRole(user, 'admin', 'superadmin');
 * ```
 *
 * This function is intentionally minimal so that it can later be extended
 * with fine-grained permissions attached to roles without changing call
 * sites.  For example, a future `hasPermission(user, 'metrics:read')`
 * could resolve the permission against a role→permissions map.
 */
export function hasAnyRole(
  user: IUser | null | undefined,
  ...required: string[]
): boolean {
  if (!user) return false;
  // Prefer the roles array; fall back to legacy `role` string for plain
  // objects deserialized from localStorage before the migration.
  // Access via index to avoid the TS deprecation diagnostic on IUser.role.
  const legacy = (user as unknown as Record<string, unknown>)['role'];
  const userRoles: string[] =
    user.roles ?? (typeof legacy === 'string' && legacy ? [legacy] : []);
  return required.some((r) => userRoles.includes(r));
}

/**
 * Check whether a user holds **all** of the required roles.
 *
 * @param user      The user object (must expose a `roles` array).
 *                  Passing `null`/`undefined` returns `false`.
 * @param required  One or more role strings the user must have.
 *
 * @returns `true` only when the user has every listed role.
 *
 * @example
 * ```ts
 * hasAllRoles(user, 'admin', 'metrics');
 * ```
 */
export function hasAllRoles(
  user: IUser | null | undefined,
  ...required: string[]
): boolean {
  if (!user) return false;
  const legacy = (user as unknown as Record<string, unknown>)['role'];
  const userRoles: string[] =
    user.roles ?? (typeof legacy === 'string' && legacy ? [legacy] : []);
  return required.every((r) => userRoles.includes(r));
}
